<template>
  <div class="column-chart">
    <svg :width="width" :height="height">
      <g :style="{transform: `translate(${margin.left}px, ${margin.top}px)`}">
        <g class="axis axis--x" :style="{transform: `translate(0, ${padded.height}px)`}"></g>
        <g class="axis axis--y" :style="{transform: `translate(${padded.left}px, 0)`}"></g>
        <rect v-for="(bar, i) in bars" :key="i" :fill="chartOptions.barColor ? chartOptions.barColor : '#cccccc'" :width="bar.width" :height="bar.height" :x="bar.x" :y="bar.y"></rect>
      </g>
    </svg>
  </div>
</template>

<script>
import isFunction from 'lodash/isFunction'
import merge from 'lodash/merge'
import identity from 'lodash/identity'

import * as d3 from 'd3';
import embeddable from '../mixins/embeddable'

// Call the first argument if it's a function, or return it
const castCall = (fnOrValue = identity, ...rest) => isFunction(fnOrValue) ? fnOrValue(...rest) : fnOrValue

const props = {
  chartData: Array,
  chartOptions: Object
};

export default {
  name: 'ColumnChart',
  mixins: [embeddable],
  props,
  data() {
    return {
      width: 0,
      height: 0,
    }
  },
  computed: {
    margin() {
      return merge({
        left: castCall(this.chartOptions.labelWidth) || 0,
        right: 0,
        top: 30,
        bottom: 50
      }, this.chartOptions.margin || {})
    },
    padded() {
      const width = this.width - this.margin.left - this.margin.right
      const height = this.height - this.margin.top - this.margin.bottom
      return {width, height}
    },
    scale() {
      const x = d3.scaleBand()
        .domain(this.chartData.map(d => d.date))
        .range([0, this.padded.width])
        .padding(.35),
      y = d3.scaleLinear()
        .domain([0, d3.max(this.chartData, d => d[this.chartOptions.seriesName])])
        .range([this.padded.height, 0]);
      return {x, y};
    },
    bars() {
      return this.chartData.map(d => {
        return {
          width: Math.abs(this.scale.x.bandwidth()),
          height: Math.abs(this.padded.height - this.scale.y(d[this.chartOptions.seriesName])),
          x: this.scale.x(d.date),
          y: this.scale.y(d[this.chartOptions.seriesName])
        }
      });
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
    this.onResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  watch: {
    width() { this.setup() },
    chartData () { this.setup() },
    chartOptions () { this.setup() }
  },
  methods: {
    setup () {
      this.initialize()
      this.update()
    },
    onResize() {
      this.width = this.$el.offsetWidth;
      this.height = this.$el.offsetWidth * this.baseHeightRatio;
    },
    initialize() {
      d3.axisLeft().scale(this.scale.y);
      d3.axisBottom().scale(this.scale.x);
    },
    update() {
      d3.select(this.$el).select(".axis--x")
        .call(d3.axisBottom(this.scale.x)
          .tickFormat(d => castCall(this.chartOptions.xAxisTickFormat, d))
        ).select(".domain").remove();

      d3.select(this.$el).select(".axis--y")
        .call(d3.axisLeft(this.scale.y)
          .tickFormat(d => castCall(this.chartOptions.yAxisTickFormat, d))
          .ticks(this.chartOptions.yAxisTicks)
        ).selectAll(".tick line").attr("x2", this.padded.width);
    }
  }
}
</script>

<style>
</style>
